/* eslint-disable camelcase */
import * as THREE from 'three'
import { COLORS, SEAT_STATUSES } from '../constants'
import { getLineMaterial, getSeatMaterial } from '../utils/helper_functions'
import { drawLabel } from './drawLabel'
import { textGeometries, materials, blocks } from '../utils/mapData'

const calculateCenterOfQuadrangle = (points) => {
  // https://en.wikipedia.org/wiki/Line%E2%80%93line_intersection
  const [
    { x: x1, y: y1 },
    { x: x4, y: y4 },
    { x: x2, y: y2 },
    { x: x3, y: y3 }
  ] = points
  const t =
    ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) /
    ((x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4))

  return [x1 + t * (x2 - x1), y1 + t * (y2 - y1)]
}

export const drawGroupBlock = (group, groups, creatorInfo, options) => {
  const {
    scene,
    scale,
    fonts,
    center_x,
    center_y,
    seat_circle_radius,
    color = COLORS.AVAILABLE,
    events,
    sceneMeshes
  } = options
  const { id, x, y } = group

  const { edges } = creatorInfo.groups[id]

  // top left -> top right -> bottom right -> bottom left
  const circularPoints = [
    new THREE.Vector3(edges.topLeft.x * scale, edges.topLeft.y * scale, 0),
    new THREE.Vector3(edges.topRight.x * scale, edges.topRight.y * scale, 0),
    new THREE.Vector3(
      edges.bottomRight.x * scale,
      edges.bottomRight.y * scale,
      0
    ),
    new THREE.Vector3(edges.bottomLeft.x * scale, edges.bottomLeft.y * scale, 0)
  ]

  // create geometry
  const geometry = new THREE.ShapeBufferGeometry(
    new THREE.Shape(circularPoints)
  )

  // create material
  const material = getSeatMaterial({ color }, materials)

  // create object from geometry and material
  const groupObj = new THREE.Mesh(geometry, material)

  // set position, rotate and add to scene
  groupObj.position.set(x * scale, -y * scale, 0)

  scene.add(groupObj)
  // sceneMeshes.push(groupObj)

  // calculate center coordinates of the group and draw label
  const [centerX, centerY] = calculateCenterOfQuadrangle(circularPoints)
  const labelText = groups[id].label || ''
  const labelPosition = [centerX + x * scale, centerY - y * scale, 0]
  drawLabel(
    labelText,
    {
      font: fonts.openSans,
      color: 'black',
      position: labelPosition,
      center_x,
      center_y,
      scale,
      seat_circle_radius,
      scene,
      size: 12
    },
    textGeometries,
    materials
  )

  // draw border
  const lineBasicMaterial = getLineMaterial({ color }, materials)
  const lineGeometry = new THREE.BufferGeometry().setFromPoints([
    ...circularPoints,
    circularPoints[0]
  ])

  const line = new THREE.Line(lineGeometry, lineBasicMaterial)
  groupObj.border = line
  groupObj.add(line)

  // attach events
  groupObj.click = events.onClick
  groupObj.mouseOver = events.onMouseOver
  groupObj.mouseOut = events.onMouseOut

  groupObj.sgId = id
  groupObj.tierId = groups[id].groups[0].units[0].tier
  groupObj.label = groups[id].label
  groupObj.availableCount = 0
  groupObj.status = SEAT_STATUSES.BOOKED
  groupObj.isSelected = false

  groupObj.changeBorderColor = (color) => {
    groupObj.border &&
      (groupObj.border.material = getLineMaterial({ color }, materials))
  }

  groupObj.changeBorderColorToDefault = () => groupObj.changeBorderColor(color)

  groupObj.select = () => {
    groupObj.isSelected = true
    groupObj.changeBorderColor(COLORS.SELECTED_BLOCK_BORDER)
  }

  groupObj.unselect = () => {
    groupObj.isSelected = false
    groupObj.changeBorderColorToDefault()
  }

  const makeAvailable = () => {
    !groupObj.isSelected && groupObj.changeBorderColor(color)

    groupObj.material = getSeatMaterial({ color }, materials)

    groupObj.status = SEAT_STATUSES.AVAILABLE
    groupObj.click = events.onClick
    groupObj.changeBorderColorToDefault = () =>
      groupObj.changeBorderColor(color)
  }

  const makeBooked = () => {
    groupObj.changeBorderColor(COLORS.BOOKED)

    groupObj.material = getSeatMaterial({ color: COLORS.BOOKED }, materials)

    groupObj.status = SEAT_STATUSES.BOOKED
    groupObj.click = null
    groupObj.changeBorderColorToDefault = () =>
      groupObj.changeBorderColor(COLORS.BOOKED)
  }

  const makeBlocked = () => {
    groupObj.changeBorderColor(COLORS.BLOCKED)

    groupObj.material = getSeatMaterial({ color: COLORS.BLOCKED }, materials)

    groupObj.status = SEAT_STATUSES.BLOCKED
    groupObj.click = null
    groupObj.changeBorderColorToDefault = () =>
      groupObj.changeBorderColor(COLORS.BLOCKED)
  }

  makeBooked()
  groupObj.statusUpdate = (status, availableCount) => {
    if (groupObj.availableCount !== availableCount) {
      groupObj.availableCount = availableCount
      switch (status) {
        case SEAT_STATUSES.AVAILABLE: {
          makeAvailable()
          break
        }
        case SEAT_STATUSES.BOOKED: {
          makeBooked()
          break
        }
        case SEAT_STATUSES.BLOCKED: {
          makeBlocked()
          break
        }
      }
    }
  }
  groupObj.hasHandlers = true
  sceneMeshes.push(groupObj)
  blocks.push(groupObj)
}
